import type { ReactNode } from 'react'
import { cssVars } from '../../cssVars'
import { formatCount } from '../../format'
import styles from './RankedBarList.module.css'

/**
 * The ranking, and the screen this console exists for.
 *
 * The flagship view is a list of the questions a community asked that published
 * knowledge could not answer, in order of how often they were asked. The
 * platform model calls `knowledge_gaps` the most valuable object in the system,
 * and the reason is in the Farmer.Chat evidence: 66% of the questions it failed
 * to answer failed because the knowledge base did not cover them, not because
 * the model was weak. A ranked list of unanswered questions is a direct readout
 * of a programme's blind spots, arriving without anybody running a survey.
 *
 * It is built from HTML rather than from SVG, and that is the decision worth
 * defending, because every other chart here is drawn.
 *
 * The labels are the data. They are sentences in Hausa, in Kanuri, in English:
 * a real question somebody asked out loud. Text inside an SVG cannot wrap, is
 * not found by the browser's find-in-page, does not reflow when the panel gets
 * narrower, and cannot be selected and pasted into the document somebody is
 * writing about it. In HTML it does all four for free, and the only thing SVG
 * would have contributed is the rectangle, which CSS draws with one gradient.
 *
 * It is also the one chart in this set with no visually-hidden table, and for
 * the right reason rather than an oversight: the labels and the counts are
 * already real text in the document. A table would make a screen reader read
 * the entire ranking twice. Only the bar is hidden, because a bar whose length
 * is already written out beside it says nothing new.
 */

export interface RankedItem {
  key: string
  /** The question, the topic, the rumour. Long, and allowed to wrap. */
  label: string
  value: number
  /** A second line: the locale it was asked in, when it was first heard. */
  meta?: ReactNode
}

export interface RankedBarListProps {
  title: string
  description?: ReactNode
  items: readonly RankedItem[]
  /**
   * The length the bars are measured against. Defaults to the largest item, so
   * the top bar fills the row. Pass the total, or last period's peak, when two
   * of these lists have to be compared with each other.
   */
  max?: number
  /** Opening the cluster behind a row. Rows become buttons when this is given. */
  onSelect?(item: RankedItem): void
  showRank?: boolean
  locale?: string
  className?: string
}

export function RankedBarList({
  title,
  description,
  items,
  max,
  onSelect,
  showRank = true,
  locale,
  className,
}: RankedBarListProps) {
  const ceiling = max ?? Math.max(1, ...items.map((item) => item.value))

  return (
    <figure className={[styles.figure, className].filter(Boolean).join(' ')}>
      <figcaption className={styles.caption}>
        <span className={styles.title}>{title}</span>
        {description ? <span className={styles.description}>{description}</span> : null}
      </figcaption>

      {/*
        `role="list"` is not redundant. Removing the markers with
        `list-style: none` also removes the list semantics in Safari, so an
        ordered list styled like this one stops announcing "list of ten" and
        stops announcing which item is which. The role puts both back, and
        base.css already carries the reset for lists that declare it.
      */}
      <ol className={styles.list} role="list">
        {items.map((item, index) => {
          const share = Math.min(100, Math.max(0, (item.value / ceiling) * 100))

          const content = (
            <>
              {showRank ? <span className={styles.rank}>{index + 1}</span> : null}
              <span className={styles.text}>
                <span className={styles.label}>{item.label}</span>
                {item.meta ? <span className={styles.meta}>{item.meta}</span> : null}
              </span>
              <span className={styles.value}>{formatCount(item.value, locale)}</span>
              {/* Decoration, strictly. The length is the count, and the count is
                  spelled out immediately before it. */}
              <span
                className={styles.bar}
                style={cssVars({ '--share': `${share}%` })}
                aria-hidden="true"
              />
            </>
          )

          return (
            <li key={item.key} className={styles.item}>
              {onSelect ? (
                <button type="button" className={styles.row} onClick={() => onSelect(item)}>
                  {content}
                </button>
              ) : (
                <div className={styles.row}>{content}</div>
              )}
            </li>
          )
        })}
      </ol>
    </figure>
  )
}
